import { useRxData } from 'rxdb-hooks';
import { escapeRegExp } from '@helper/utils';
import { MangoQuery, RxCollection } from 'rxdb/dist/types/types';
import { MangoQuerySelector } from 'rxdb';
import { useCallback } from 'react';
import { useDebounceSearch } from '@hooks/useDebounceSearch';
import { getFilter } from '@hooks/useFilterData';
import { useFilterStore } from '@store/filter';
import { SelectorFilter } from '@store/memberList';
import { ILmsUserLeadResponse, ILmsUserLeadSearchFilter, ILmsUserLeadResultType } from '.';
import { useRxCountQuery } from '@hooks/useCount';
import { LmsUserListFilterStore, useLmsUserListFilterStore } from '@store/lms/userListFilterStore';

export interface ILmsUserLeadsFilterProps {
	searchValue?: string;
	sortBy?: string;
	createddate?: [Date | null, Date | null];
	status?: string;
	competition_tag?: boolean;
	source?: string;
	referral_source_c?: string;
	rigidGluePotential?: boolean;
}

export const useLmsUserLeadsRxDb = (props: ILmsUserLeadsFilterProps): ILmsUserLeadResultType => {
	const {
		searchValue = '',
		sortBy,
		createddate,
		status,
		competition_tag,
		source,
		referral_source_c,
		rigidGluePotential,
	} = props;
	const queryConstructor = useCallback(
		(collection: RxCollection<ILmsUserLeadResponse>) => {
			let querySelector: MangoQuery<ILmsUserLeadResponse> = {
				selector: {
					$or: [
						{
							name: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							phone: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
						{
							id: { $regex: `.*${searchValue}.*`, $options: 'i' },
						},
					],
				},
				sort: [{ createddate: 'desc' }],
			};

			if (querySelector.selector && competition_tag !== false) {
				querySelector.selector.competition_tag = { $eq: competition_tag };
			}

			if (querySelector.selector && status && status.trim() !== '') {
				querySelector.selector.lead_status = { $eq: status };
			}
			if (querySelector.selector && source?.trim()) {
				querySelector.selector.source = { $eq: source };
			}
			if (querySelector.selector && referral_source_c?.trim()) {
				querySelector.selector.referral_source_c = { $eq: referral_source_c };
			}
			if (querySelector.selector && createddate) {
				const [startDate, endDate] = createddate;
				if (startDate && endDate) {
					querySelector.selector.createddate = {
						$gte: startDate.toISOString(),
						$lte: endDate.toISOString(),
					};
				}
			}
			if (querySelector.selector && rigidGluePotential) {
				querySelector.selector.glue_consumption_per_month = { $gte: 50 };
			}

			if (sortBy) {
				querySelector = {
					...querySelector,
					sort: [{ [sortBy]: 'desc' }],
				};
			}
			return collection.find(querySelector);
		},
		[competition_tag, createddate, searchValue, sortBy, status, source, referral_source_c, rigidGluePotential]
	);

	const totalCount = useRxCountQuery<ILmsUserLeadResponse>('lms_user_leads', queryConstructor);

	const { result, isFetching, fetchMore } = useRxData<ILmsUserLeadResponse>('lms_user_leads', queryConstructor, {
		pageSize: 20,
		pagination: 'Infinite',
	});

	return {
		result,
		isFetching,
		fetchMore,
		totalCount,
	};
};

// Legacy function for backward compatibility
export const GetLmsUserLeadsData = (name: string): ILmsUserLeadResultType => {
	const { sortValue, filterObject, search } = useFilterStore((state) => state);
	const debouncedSearch = useDebounceSearch(search);
	const { lmsUserListFilter } = useLmsUserListFilterStore() as LmsUserListFilterStore;

	const queryConstructor = useCallback(
		(collection: RxCollection<ILmsUserLeadResponse>) => {
			const { selectorData } = getFilter(filterObject);
			type CombinedSelectorType = SelectorFilter & ILmsUserLeadSearchFilter & MangoQuerySelector<ILmsUserLeadResponse>;

			const selector: CombinedSelectorType = selectorData as CombinedSelectorType;

			if (debouncedSearch && debouncedSearch.trim() !== '') {
				const searchData = escapeRegExp(debouncedSearch);
				selector.$or = [
					{ name: { $regex: `.*${searchData}.*`, $options: 'i' } },
					{ phone: { $regex: `.*${searchData}.*`, $options: 'i' } },
				];
			}

			if (lmsUserListFilter) {
				if (lmsUserListFilter.competition_tag !== undefined) {
					selector.competition_tag = { $eq: lmsUserListFilter.competition_tag };
				}

				if (lmsUserListFilter.status && lmsUserListFilter.status.trim() !== '') {
					selector.status = { $eq: lmsUserListFilter.status };
				}

				if (lmsUserListFilter.createddate) {
					const [startDate, endDate] = lmsUserListFilter.createddate;
					if (startDate && endDate) {
						selector.createddate = {
							$gte: startDate.toISOString(),
							$lte: endDate.toISOString(),
						};
					}
				}

				if (lmsUserListFilter.rigidGluePotential) {
					selector.glue_consumption_per_month = { $gte: 50 };
				}
			}

			(Object.keys(selector) as Array<keyof (SelectorFilter & ILmsUserLeadSearchFilter)>).forEach((key) => {
				if (selector[key] === undefined || selector[key] === null) {
					delete selector[key];
				}
			});

			const defaultSelector = Object.keys(selector).length > 0 ? selector : {};
			let query = collection.find({ selector: defaultSelector });

			if (sortValue) {
				const isSpecialSort = ['pwg_banking_qtd', 'glue_consumption_per_month'].includes(sortValue);
				const sortDirection =
					isSpecialSort || (name === 'lms_user_leads' && sortValue === 'createddate') ? 'desc' : 'asc';

				query = query.sort({ [sortValue]: sortDirection });
			}
			query = query.sort({ createddate: 'desc' });

			return query;
		},
		[filterObject, debouncedSearch, sortValue, name, lmsUserListFilter]
	);

	const totalCount = useRxCountQuery<ILmsUserLeadResponse>(name, queryConstructor);

	const { result, isFetching, fetchMore } = useRxData<ILmsUserLeadResponse>(name, queryConstructor, {
		pageSize: 20,
		pagination: 'Infinite',
	});

	return { result, fetchMore, isFetching, totalCount };
};
